import { getFrameAncestorsFromProjectConfig } from "server/models/util/projectConfig";
import { withIronSessionSsr } from "iron-session/next";
import { GetServerSideProps } from "next";
import { prisma } from "server/data/prisma";
import { embeddedSessionConfig } from "server/sessions/editor";
import { isCustomDomain } from "utils/project-helpers";
import { z } from "zod";
import { getCSPHeader } from "get-csp-header";
import { ProjectAuthType } from "@prisma/client";
import { PublishedPage } from "components/PublishedPage";
import AppProvider from "components/AppProvider";

const ParsedUrlQuerySchema = z.object({
  projectSlug: z.string(),
  pageId: z.string().cuid(),
  noBackground: z.string().optional(),
});

type Props = {
  noBackground: boolean;
};

export default function EmbeddedPublishedPage(props: Props) {
  return (
    <AppProvider hideProgress skipPageViewEvent>
      <PublishedPage isEmbed={true} noBackground={props.noBackground} />
    </AppProvider>
  );
}

// TODO: Pare this down so that it is only setting the Content-Security-Policy header
export const getServerSideProps: GetServerSideProps = withIronSessionSsr(
  async function getServerSidePropsInner(context) {
    // Parse url params
    const parsedQueryResult = ParsedUrlQuerySchema.safeParse(context.query);
    if (!parsedQueryResult.success) return { notFound: true };
    const query = parsedQueryResult.data;

    // Get project
    const project = await prisma.project.findUnique({
      include: {
        _count: { select: { pages: true } },
      },
      where: isCustomDomain(query.projectSlug)
        ? { customDomain: query.projectSlug }
        : { slug: query.projectSlug },
    });
    if (!project) return { notFound: true };

    // Don't allow embedding for projects with passwords
    if (project.authType !== ProjectAuthType.None) {
      context.res.statusCode = 403;
    }

    // Set frame-ancestors Content-Security-Policy header
    const frameAncestors = getFrameAncestorsFromProjectConfig(project);
    context.res.setHeader(
      "Content-Security-Policy",
      getCSPHeader(frameAncestors)
    );

    // Return props
    return {
      props: {
        noBackground: query.noBackground === "true",
      },
    };
  },
  embeddedSessionConfig
);
